/**
 * Modal URL Builder — URL parsing and validation helpers
 *
 * Reads modal context out of URL search params, extracts entity IDs,
 * and validates parameter shape for configured modals.
 */

import {
  getModalConfig,
  isModalConfigured,
  extractEntityId,
} from "./modal-url-config";
import type { ValidationResult } from "./modal-url-types";

/**
 * Parse modal parameters from a URL
 *
 * Extracts the modal ID and all relevant parameters from URL search parameters
 *
 * @param searchParams - URL search parameters to parse
 * @param expectedModalId - Optional modal ID to validate against
 * @returns Object containing modal ID and parameters, or null if no modal in URL
 *
 * @example
 * ```tsx
 * const params = new URLSearchParams('?modal=deals-detail&dealsId=123&tab=overview');
 * const result = parseModalUrl(params);
 * // => { modalId: 'deals-detail', params: { dealsId: '123', tab: 'overview' } }
 *
 * // With validation
 * const result = parseModalUrl(params, 'deals-detail');
 * // => { modalId: 'deals-detail', params: { dealsId: '123', tab: 'overview' } }
 * // Would return null if modalId doesn't match
 * ```
 */
export function parseModalUrl(
  searchParams: URLSearchParams | null | undefined,
  expectedModalId?: string
): { modalId: string; params: Record<string, string> } | null {
  if (!searchParams) return null;

  const modalId = searchParams.get("modal");
  if (!modalId) return null;

  // Validate against expected modal ID if provided
  if (expectedModalId && modalId !== expectedModalId) {
    return null;
  }

  // Extract all parameters except 'modal'
  const params: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (key !== "modal") {
      params[key] = value;
    }
  });

  return { modalId, params };
}

/**
 * Check if a URL contains a specific modal
 *
 * @param searchParams - URL search parameters to check
 * @param modalId - Modal ID to look for
 * @returns true if the URL contains the specified modal
 */
export function hasModalInUrl(
  searchParams: URLSearchParams | null | undefined,
  modalId: string
): boolean {
  if (!searchParams) return false;
  return searchParams.get("modal") === modalId;
}

/**
 * Get entity ID from URL for a specific modal
 *
 * Convenience function that extracts just the entity ID from URL parameters
 *
 * @param searchParams - URL search parameters
 * @param modalId - Modal ID to extract entity ID for
 * @returns Entity ID string or null if not found
 */
export function getEntityIdFromUrl(
  searchParams: URLSearchParams | null | undefined,
  modalId: string
): string | null {
  return extractEntityId(modalId, searchParams);
}

/**
 * Validate modal URL parameters
 *
 * Checks if URL parameters are valid for a given modal
 *
 * @param modalId - Modal ID to validate against
 * @param params - Parameters to validate
 * @returns Validation result with any errors
 */
export function validateModalParams(
  modalId: string,
  params: Record<string, string | undefined>
): ValidationResult {
  const errors: string[] = [];

  // Check if modal is configured
  if (!isModalConfigured(modalId)) {
    errors.push(`Modal "${modalId}" is not configured`);
    return { valid: false, errors };
  }

  const config = getModalConfig(modalId);

  // Check for entity ID (at least one form must be present for detail modals)
  if (modalId.endsWith("-detail")) {
    const hasEntityId = params[config.singularParam] || params[config.pluralParam];
    if (!hasEntityId) {
      errors.push(`Entity ID is required for detail modal (${config.singularParam} or ${config.pluralParam})`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
